/**
 * The Descope Clinical app permissions that gate EBP.
 *
 * The names are defined in Descope (and mirrored in the platform monorepo's
 * own `ClinicalAppPermission` enum, which lists every Clinical permission);
 * EBP mirrors only the `crm_` ones that gate its own products. Descope
 * derives a user's permissions from their roles, and both the API and the web
 * apps read them straight off the Descope session token (PPL-3826). The
 * Firebase custom token EBP still mints (see `postDescope`) is for Firestore/
 * Storage subscriptions only and carries just the rule subset
 * (`firestoreSubscriptionPermissions`), not this whole set.
 */
export enum ClinicalAppPermission {
  /** Sign in to EBP; view own profile (read-only). */
  CrmAccess = 'crm_access',

  /**
   * Open the case list and any case's details page. Includes reading the
   * organization settings needed to render the page.
   */
  CrmCasesRead = 'crm_cases_read',
  /** Edit fields on the case record. */
  CrmCasesDetailsManage = 'crm_cases_details_manage',
  /** Discharge a case, and undo a discharge. */
  CrmCasesDischargeManage = 'crm_cases_discharge_manage',
  /** Change which expert owns the case. */
  CrmCasesExpertAssign = 'crm_cases_expert_assign',
  /**
   * Set the support tier (Level 1/2/3) the member's organization has funded
   * - drives max appointments, support end date, estimated discharge date.
   */
  CrmCasesSupportLevelManage = 'crm_cases_support_level_manage',
  /**
   * Record whether the member consents to their case data being shared with
   * the insurer / employer.
   */
  CrmCasesDataSharingConsentManage = 'crm_cases_data_sharing_consent_manage',

  /**
   * Add a time-tracked note to any case, and edit the notes you wrote.
   * Editing someone else's is refused - the note's `expertId` must match
   * yours.
   */
  CrmNotesOwnManage = 'crm_notes_own_manage',

  /** Edit MyCaW intake / outcome responses and set the safeguarding check. */
  CrmFormsManage = 'crm_forms_manage',

  /** Open and download the care documents generated on a case. */
  CrmCareDocumentsRead = 'crm_care_documents_read',
  /**
   * Create and edit drafts of all six types: PCP, work-focused plan,
   * return-to-work plan, initial assessment, interim care, end-of-care
   * summary.
   */
  CrmCareDocumentsEdit = 'crm_care_documents_edit',
  /**
   * Use AI to draft those documents (PCP, initial assessment, interim care,
   * end-of-care).
   */
  CrmCareDocumentsAiDraft = 'crm_care_documents_ai_draft',
  /**
   * Send a finished document to the Platform (Medplum) - care documents and
   * referral documents.
   */
  CrmDocumentsPublish = 'crm_documents_publish',

  /**
   * Set the insurer support authorization on a case: not required, rejected,
   * or level 1 / 2 / 3. Not the support tier itself - that is
   * `crm_cases_support_level_manage`.
   */
  CrmAuthorizationsManage = 'crm_authorizations_manage',

  /**
   * See which system emails were sent on a case or referral, to whom, and
   * whether they were delivered. No message bodies.
   */
  CrmEmailDeliveryRead = 'crm_email_delivery_read',
  /** Resend a templated email, and refresh its delivery status. */
  CrmEmailDeliveryResend = 'crm_email_delivery_resend',

  /**
   * Mark a follow-up as done; record the decision on a referral requested
   * during an appointment. Not scheduling - appointments are created by the
   * Platform.
   */
  CrmAppointmentsOutcomeManage = 'crm_appointments_outcome_manage',

  /**
   * See the "Orgs" section and open an organization's settings page. The
   * Orgs page only: the settings a case page needs to render (discharge,
   * support levels, RTW forms, care-document types) are read with the case,
   * under `crm_cases_read`, by every clinician.
   */
  CrmOrganizationsSettingsRead = 'crm_organizations_settings_read',
  /** Change or delete an organization's settings groups. */
  CrmOrganizationsSettingsManage = 'crm_organizations_settings_manage',

  /** Open a referral form and send a referral. Held by referrers, not staff. */
  CrmReferrer = 'crm_referrer',
  /** The referral list and details, with the patient details redacted. */
  CrmReferralsRead = 'crm_referrals_read',
  /** See the patient details that show as `<Encrypted>` otherwise. */
  CrmReferralsPatientDetailsRead = 'crm_referrals_patient_details_read',
  /** Edit a referral's fields. */
  CrmReferralsDetailsManage = 'crm_referrals_details_manage',
  /** Cancel and pause a referral. */
  CrmReferralsStatusManage = 'crm_referrals_status_manage',
  /** Record the patient's consent decision. */
  CrmReferralsConsentManage = 'crm_referrals_consent_manage',
}

// No `crm_expert`: the expert directory (`GET /experts`, the clinicians a
// case can be assigned to) is the Clinical Platform's nurses (PPL-3825), not
// a permission. The coarse `expert` / `admin` claims EBP once derived from
// `crm_expert` / `crm_admin` are gone too (PPL-3637); every route and page
// asks for one of the permissions above.

/**
 * EBP's own permissions are the `crm_`-prefixed ones. A Clinical session also
 * carries the permissions of the other products; EBP reads only its own off
 * the session token and ignores the rest.
 */
export const CRM_PERMISSION_PREFIX = 'crm_';

/** The CRM permissions of a Descope session, ignoring anything malformed. */
export const readCrmPermissions = (permissions: unknown): string[] =>
  Array.isArray(permissions)
    ? permissions.filter(
        (permission): permission is string =>
          typeof permission === 'string' &&
          permission.startsWith(CRM_PERMISSION_PREFIX),
      )
    : [];

/** Whether a claimed permission set grants `permission`. */
export const hasClinicalAppPermission = (
  permissions: unknown,
  permission: ClinicalAppPermission,
): boolean => Array.isArray(permissions) && permissions.includes(permission);

// The permissions the Firestore and Storage security rules read off
// `request.auth.token.permissions`. Since the API now authorizes on the
// Descope session token directly, the Firebase token EBP still mints exists
// only so the web can subscribe to Firestore and read Storage — so its custom
// claims carry just these, not the full CRM set. Keep in step with
// firestore.rules and storage.rules: a permission a rule consults that is
// missing here would make a legitimate subscription silently return nothing.
export const FIRESTORE_SUBSCRIPTION_PERMISSIONS: readonly ClinicalAppPermission[] =
  [
    ClinicalAppPermission.CrmAccess,
    ClinicalAppPermission.CrmCasesRead,
    ClinicalAppPermission.CrmCareDocumentsRead,
    ClinicalAppPermission.CrmEmailDeliveryRead,
    ClinicalAppPermission.CrmOrganizationsSettingsRead,
    ClinicalAppPermission.CrmReferralsRead,
    // NOT crm_referrals_patient_details_read: the rules gate referral data on
    // crm_referrals_read alone; the patient-details permission gates the API
    // endpoint that decrypts them (server-side, on the Descope bearer), no
    // rule reads it. Keeping it here would re-mint the subscription token
    // pointlessly whenever it changed.
  ];

/**
 * The subset of `permissions` the security rules consult — the only claims
 * the Firebase subscription token needs to carry. Well under Firebase's
 * 1000-byte custom-claims limit, unlike the whole CRM set.
 */
export const firestoreSubscriptionPermissions = (
  permissions: unknown,
): string[] =>
  readCrmPermissions(permissions).filter((permission) =>
    FIRESTORE_SUBSCRIPTION_PERMISSIONS.includes(
      permission as ClinicalAppPermission,
    ),
  );
